/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, makeObservable, observable } from 'mobx';

import type { Feature } from '@veeam-vspc/models/web-controllers';

import { BaseAddon } from '../base-addon';
import { FeaturesSupplier } from '../../suppliers/rest';
import { addParamsToQuery, isErrorThatNeedToBeShown } from '../../core';

import type { PortalFeaturesConfig } from './interfaces';
import type { AddonContext } from '../base-addon';
import type { RequestSuccessResponse } from '../../interfaces';
import type { FeatureNames } from './enums';

export class PortalFeaturesAddon extends BaseAddon {

    protected readonly url: string;
    protected readonly brandingUrl: string | null;
    protected readonly featuresSupplier: FeaturesSupplier;

    @observable features: { [key in FeatureNames]?: Feature } = {};

    constructor(addonContext: AddonContext, portalFeaturesConfig: PortalFeaturesConfig = {}) {
        super(addonContext);

        makeObservable(this);

        const {
            url = '/features',
            brandingUrl = null,
        } = portalFeaturesConfig ?? {};

        this.url = url;
        this.brandingUrl = brandingUrl;
        this.featuresSupplier = new FeaturesSupplier(this.services.transportService);
    }

    @action
    init(): Promise<void> {
        const url = addParamsToQuery(this.url, this.brandingUrl ? { brandingUrl: this.brandingUrl } : {});

        if (!this.noAuth) {
            return this.featuresSupplier.getFeatures(url, { disableGlobalFilters: true })
                .then((resp: RequestSuccessResponse<Feature[]>) => {
                    this.setFeatures(resp.data);
                })
                .catch((err) => {
                    isErrorThatNeedToBeShown(err) && console.error(err);
                });
        }

        this.setFeatures([]);

        return Promise.resolve();
    }

    @action
    destroy(): void {
        this.setFeatures([]);
    }

    @action.bound
    setFeatures(value: Feature[]): void {
        this.features = {};

        value.forEach(({ id: featureId, ...restData }) => {
            this.features[featureId] = { featureId, ...restData };
        });
    }

    isFeatureEnabled(featureId: FeatureNames): boolean {
        return this.features[featureId] && !this.features[featureId].unsupported;
    }

}
